import { RegistryTelegramChannels } from './RegistryTelegramChannels';
import {
  Alert,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TablePagination,
  TextField,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { RegistryCatalogQuery } from '../../../api/src/registry/registry-catalog.types';
import { PLATFORM_IDS, RESOURCE_PLATFORMS } from '../../../api/src/registry/registry-platform';
import type { RegistryResource } from '../../../api/src/registry/registry.types';
import { api } from '../api/client';
import { useCanEdit } from '../components/EditorOnly';
import { PageHeader } from '../components/PageHeader';
import { Stack, Typography } from '../components/mui-system';
import { useWorkspace } from '../workspace-context';
import { RegistryAiPlaceholder } from './RegistryAiPlaceholder';
import { RegistryCatalogCard } from './RegistryCatalogCard';
import {
  RegistryCatalogTable,
  registryManagementLabels,
  registryOwnershipLabels,
} from './RegistryCatalogTable';
import { RegistryImportDialog } from './RegistryImportDialog';
import { RegistryResourceDialog } from './RegistryResourceDialog';
import { useRegistryData } from './useRegistryData';

const filters = [
  {
    key: 'platform',
    label: 'Платформа',
    values: Object.fromEntries(PLATFORM_IDS.map((id) => [id, RESOURCE_PLATFORMS[id].label])),
  },
  { key: 'ownership', label: 'Принадлежность', values: registryOwnershipLabels },
  { key: 'management', label: 'Управление', values: registryManagementLabels },
  { key: 'donor', label: 'Донорские публикации', values: { yes: 'Есть', no: 'Нет известных' } },
  {
    key: 'discovery',
    label: 'Где обнаружен',
    values: { serp: 'Поисковая выдача', unseen: 'Нет наблюдений', ai: 'Ответы ИИ · скоро' },
  },
  { key: 'engine', label: 'Поисковая система', values: { google: 'Google', yandex: 'Яндекс' } },
  { key: 'role', label: 'Отношение к бренду', values: { competitor: 'Конкуренты' } },
] as const;

export function RegistryPage() {
  const { activeWorkspace, user } = useWorkspace();
  return (
    <RegistryWorkspace
      key={`${activeWorkspace!.id}:${user.role}`}
      workspaceId={activeWorkspace!.id}
    />
  );
}
function RegistryWorkspace({ workspaceId }: { workspaceId: string }) {
  const [params, setParams] = useSearchParams();
  const canEdit = useCanEdit('registry.create');
  const canImport = useCanEdit('registry.import');
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const queryText = params.get('q') ?? params.get('filter') ?? params.get('search') ?? '';
  const entryId = params.get('entry');
  const cardKey = params.get('resource');
  const telegramChannel = params.get('telegram');
  const values = Object.fromEntries(
    filters.map((filter) => {
      const value = params.get(filter.key) ?? '';
      return [filter.key, Object.hasOwn(filter.values, value) ? value : ''];
    }),
  );
  const ai = values.discovery === 'ai';
  const requestedPage = Number(params.get('page'));
  const query: RegistryCatalogQuery = {
    search: queryText,
    page:
      Number.isSafeInteger(requestedPage) && requestedPage > 0 && requestedPage <= 1000000
        ? requestedPage
        : 1,
    ...Object.fromEntries(Object.entries(values).filter(([, value]) => value)),
  };
  const queryKey = JSON.stringify(query);
  const request = useCallback(
    () =>
      ai
        ? Promise.resolve(null)
        : api.registryCatalog(workspaceId, JSON.parse(queryKey) as RegistryCatalogQuery),
    [workspaceId, queryKey, ai],
  );
  const { data, error, loading, refresh } = useRegistryData(request);
  function change(key: string, value: string) {
    const next = new URLSearchParams(params);
    for (const legacy of ['view', 'pool', 'filter', 'search', 'from', 'scope']) next.delete(legacy);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page' && key !== 'resource') next.delete('page');
    if (key === 'discovery' && value === 'ai') next.delete('engine');
    setParams(next, { replace: key === 'q' });
  }
  function closeEntry() {
    setCreating(false);
    const next = new URLSearchParams(params);
    next.delete('entry');
    setParams(next);
  }
  function saved(row: RegistryResource, archived = false) {
    setCreating(false);
    setParams(archived ? {} : { q: row.address.slice(0, 500) });
    refresh();
  }
  function closeTelegram() {
    change('telegram', '');
    refresh();
  }
  function resourcePath(key: string) {
    const next = new URLSearchParams(params);
    next.set('resource', key);
    return `/registry?${next}`;
  }
  return (
    <>
      <PageHeader
        title="Реестр"
        description="Сайты, каналы и страницы бренда — с публикациями и присутствием в поиске."
        action={
          <>
            <Button variant="outlined" onClick={() => change('telegram', 'all')}>
              Каналы Telegram
            </Button>
            {canEdit && (
              <Button variant="outlined" onClick={() => setImporting(true)}>
                Импорт ресурсов
              </Button>
            )}
            {canImport && (
              <Button variant="contained" onClick={() => setCreating(true)}>
                Добавить ресурс
              </Button>
            )}
          </>
        }
      />
      <Stack gap={2}>
        <Card variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
          <Stack gap={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
              <TextField
                size="small"
                label="Поиск ресурсов и публикаций"
                placeholder="Название, сайт, канал или URL статьи"
                value={queryText}
                disabled={ai}
                onChange={(event) => change('q', event.target.value)}
                sx={{ flex: 1 }}
                slotProps={{ htmlInput: { maxLength: 500 } }}
              />
              <Button onClick={() => setParams({})}>Сбросить фильтры</Button>
              <Button onClick={refresh} disabled={loading || ai}>
                Обновить
              </Button>
            </Stack>
            <Stack direction="row" gap={1.5} flexWrap="wrap">
              {filters.map((filter) => (
                <TextField
                  key={filter.key}
                  select
                  size="small"
                  label={filter.label}
                  value={values[filter.key]}
                  onChange={(event) => change(filter.key, event.target.value)}
                  disabled={ai && filter.key !== 'discovery'}
                  sx={{
                    minWidth: filter.key === 'donor' || filter.key === 'discovery' ? 205 : 170,
                    flex: '1 1 auto',
                  }}
                >
                  <MenuItem value="">Все</MenuItem>
                  {Object.entries(filter.values).map(([id, label]) => (
                    <MenuItem key={id} value={id}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              ))}
            </Stack>
          </Stack>
        </Card>
        {ai ? (
          <RegistryAiPlaceholder />
        ) : (
          <>
            {error && (
              <Alert severity="error">
                {error}
                {data ? ' Показан последний успешно загруженный список.' : ''}
              </Alert>
            )}
            {loading && <CircularProgress size={24} aria-label="Загрузка реестра" />}
            {data && (
              <Card variant="outlined">
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  px={2.5}
                  py={2}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    Ресурсы · {data.total}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Публикации и найденные URL — внутри карточки
                  </Typography>
                </Stack>
                {data.items.length ? (
                  <RegistryCatalogTable items={data.items} resourcePath={resourcePath} />
                ) : (
                  <Stack p={3} gap={1}>
                    <Typography fontWeight={600}>Ресурсов по этим условиям нет</Typography>
                    <Typography color="text.secondary">
                      Измените фильтры или добавьте ресурс вручную. Свободные домены доступны в
                      управлении пулом.
                    </Typography>
                  </Stack>
                )}
                {data.total > data.pageSize && (
                  <TablePagination
                    component="div"
                    count={data.total}
                    page={data.page - 1}
                    rowsPerPage={data.pageSize}
                    rowsPerPageOptions={[data.pageSize]}
                    onPageChange={(_, next) => change('page', String(next + 1))}
                  />
                )}
              </Card>
            )}
            <Typography variant="caption" color="text.secondary">
              Поисковые наблюдения берутся из сохранённых снимков «Карты выдачи». Ресурс можно
              внести заранее, до первого обнаружения.
            </Typography>
          </>
        )}
      </Stack>
      {telegramChannel && (
        <Dialog open fullWidth maxWidth="lg" onClose={closeTelegram}>
          <DialogTitle>Каналы Telegram в Реестре</DialogTitle>
          <DialogContent>
            <RegistryTelegramChannels
              key={`${workspaceId}:${telegramChannel}`}
              workspaceId={workspaceId}
              channelId={telegramChannel === 'all' ? null : telegramChannel}
              onClearChannel={() => change('telegram', 'all')}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeTelegram}>Закрыть</Button>
          </DialogActions>
        </Dialog>
      )}
      {cardKey && !entryId && !telegramChannel && (
        <RegistryCatalogCard
          key={cardKey}
          workspaceId={workspaceId}
          resourceKey={cardKey}
          onClose={() => change('resource', '')}
          onChanged={refresh}
        />
      )}
      {importing && (
        <RegistryImportDialog
          workspaceId={workspaceId}
          onClose={() => setImporting(false)}
          onImported={() => {
            setParams({});
            refresh();
          }}
        />
      )}
      {creating && (
        <RegistryResourceDialog
          workspaceId={workspaceId}
          initialKind="domain"
          onClose={closeEntry}
          onSaved={saved}
        />
      )}
      {entryId && (
        <RegistryEntry
          workspaceId={workspaceId}
          id={entryId}
          onClose={closeEntry}
          onSaved={saved}
        />
      )}
    </>
  );
}
function RegistryEntry({
  workspaceId,
  id,
  onClose,
  onSaved,
}: {
  workspaceId: string;
  id: string;
  onClose: () => void;
  onSaved: (row: RegistryResource, archived?: boolean) => void;
}) {
  const request = useCallback(() => api.registryResource(workspaceId, id), [workspaceId, id]);
  const { data, error, loading, refresh } = useRegistryData(request);
  if (data)
    return (
      <RegistryResourceDialog
        resource={data}
        workspaceId={workspaceId}
        initialKind={data.kind}
        onClose={onClose}
        onSaved={onSaved}
      />
    );
  return (
    <Stack direction="row" gap={1} my={2}>
      {loading && <CircularProgress size={24} aria-label="Загрузка карточки" />}
      {error && <Alert severity="error">{error}</Alert>}
      <Button disabled={loading} onClick={refresh}>
        Повторить
      </Button>
      <Button onClick={onClose}>Закрыть карточку</Button>
    </Stack>
  );
}
